import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Container, Card, Table } from 'react-bootstrap';
import io from 'socket.io-client';

import logo from '../../assets/images/logo-new.png';
import MainTemplate from '../../shared/templates/MainTemplate/MainTemplateContainer';
import { BASE_URL, WS_URL } from '../../config';
import './LiveResults.css';

const LiveResults = ({ match }) => {
    const [surveyData, setSurveyData] = useState(null);
    const [questionsData, setQuestionsData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [connected, setConnected] = useState(false);
    const [totalVotes, setTotalVotes] = useState(0);
    const [pageLoading, setPageLoading] = useState(true);
    const [tableLoading, setTableLoading] = useState(false);
    const socketRef = useRef(null);

    const surveyCode = new URLSearchParams(window.location.search).get('survey_code') || match.params.surveyCode;

    // Fetch initial survey data
    const fetchSurveyData = async (isInitial = false) => {
        try {
            if (isInitial) {
                setPageLoading(true);
            } else {
                setTableLoading(true);
            }
            
            console.log('📡 Fetching survey data for:', surveyCode);
            const response = await fetch(`${BASE_URL}/fetch_survey`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    surveyCode: surveyCode
                })
            });
            const data = await response.json();

            console.log('📊 Survey API response:', data);

            if (!data.error) {
                setSurveyData(data.surveyData);
                setQuestionsData(data.questionsData);
                calculateTotalVotes(data.questionsData);
            } else {
                setError(data.message);
            }
        } catch (err) {
            console.error('❌ API fetch error:', err);
            setError('Failed to fetch survey data');
        } finally {
            if (isInitial) {
                setPageLoading(false);
            } else {
                setTableLoading(false);
            }
        }
    };

    // Calculate total votes across all questions
    const calculateTotalVotes = (questions) => {
        let total = 0;
        questions.forEach(q => {
            if (q.votes) {
                total += Object.values(q.votes).reduce((sum, count) => sum + parseInt(count || 0), 0);
            }
        });
        setTotalVotes(total);
    };

    // Initialize Socket.io connection
    const initSocket = () => {
        if (socketRef.current?.connected) return;

        try {
            console.log('🔗 Connecting to Socket.io server:', WS_URL);
            
            // Create socket connection
            socketRef.current = io(WS_URL, {
                transports: ['websocket', 'polling'],
                timeout: 5000,
                reconnection: true,
                reconnectionDelay: 1000,
                reconnectionAttempts: 5
            });

            socketRef.current.on('connect', () => {
                console.log('🔗 Socket.io connected');
                setConnected(true);

                // Subscribe to survey updates
                if (surveyData?.survey_id) {
                    console.log('📝 Joining survey room:', surveyData.survey_id);
                    socketRef.current.emit('join_survey', {
                        survey_id: surveyData.survey_id,
                        survey_code: surveyCode
                    });
                }
            });

            socketRef.current.on('live_stats', (message) => {
                try {
                    console.log('📊 Live update received:', message);
                    
                    if (message.survey_id == surveyData?.survey_id) {
                        // Update with the correct data structure from Socket.io
                        if (message.data && message.data.questions_data) {
                            setTableLoading(true);
                            setQuestionsData(message.data.questions_data);
                            calculateTotalVotes(message.data.questions_data);
                            setTimeout(() => setTableLoading(false), 500); // brief loading effect
                        }
                    }
                } catch (error) {
                    console.error('❌ Socket.io message error:', error);
                }
            });

            socketRef.current.on('disconnect', (reason) => {
                console.log('🔌 Socket.io disconnected:', reason);
                setConnected(false);
            });

            socketRef.current.on('connect_error', (error) => {
                console.error('🚨 Socket.io connection error:', error);
                setConnected(false);
            });

            socketRef.current.on('reconnect', (attemptNumber) => {
                console.log('🔄 Socket.io reconnected after', attemptNumber, 'attempts');
                setConnected(true);
                
                // Re-subscribe to survey updates
                if (surveyData?.survey_id) {
                    socketRef.current.emit('join_survey', {
                        survey_id: surveyData.survey_id,
                        survey_code: surveyCode
                    });
                }
            });

        } catch (error) {
            console.error('❌ Socket.io connection failed:', error);
            setConnected(false);
        }
    };

    // Fallback polling for updates
    useEffect(() => {
        if (!surveyData || surveyData.is_live !== "1") return;

        const pollInterval = setInterval(() => {
            console.log('⏰ Polling for updates...');
            fetchSurveyData(false);
        }, 5000); // Poll every 5 seconds as fallback

        return () => clearInterval(pollInterval);
    }, [surveyData]);

    // Get vote count for a specific option
    const getVoteCount = (questionData, optionKey) => {
        if (!questionData.votes) return 0;
        return parseInt(questionData.votes[optionKey] || 0);
    };

    // Get total votes for a question
    const getQuestionTotal = (questionData) => {
        if (!questionData.votes) return 0;
        return Object.values(questionData.votes).reduce((sum, count) => sum + parseInt(count || 0), 0);
    };

    // Calculate percentage for progress bar
    const getPercentage = (votes, total) => {
        return total > 0 ? Math.round((votes / total) * 100) : 0;
    };

    // Get option text from options object
    const getOptionText = (questionData, optionKey) => {
        if (!questionData.options) return optionKey;
        return questionData.options[optionKey] || optionKey;
    };

    useEffect(() => {
        if (surveyCode) {
            fetchSurveyData(true);
        } else {
            setError('No survey code provided');
            setPageLoading(false);
        }

        return () => {
            if (socketRef.current) {
                socketRef.current.close();
            }
        };
    }, [surveyCode]);

    useEffect(() => {
        if (surveyData && surveyData.is_live === "1") {
            initSocket();
        }
    }, [surveyData]);

    if (pageLoading) {
        return (
            <div className="live-results-container">
                <div className="loading-spinner">
                    <div className="spinner"></div>
                    <p>Loading survey results...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="live-results-container">
                <div className="error-message">
                    <h2>❌ Error</h2>
                    <p>{error}</p>
                    <button onClick={() => window.location.reload()} className="retry-btn">
                        Try Again
                    </button>
                </div>
            </div>
        );
    }

    if (!surveyData) {
        return (
            <div className="live-results-container">
                <div className="error-message">
                    <h2>Survey Not Found</h2>
                    <p>The survey with code "{surveyCode}" could not be found.</p>
                </div>
            </div>
        );
    }

    return (
        <MainTemplate>
            <section className="section__thanks">
                <Container className="d-flex justify-content-center align-items-center text-center h-100">
                    <Card className="card__container">
                        <Card.Body>
                            <Link to="/"><img className="cursor-pointer" src={logo} alt="logo" /></Link>

                            <div className="pb-3 h-100 d-flex justify-content-center flex-column">

                                {/* Live Status Indicator */}
                                <div className="live-indicator mb-3">
                                    <span className={`status-dot ${connected ? 'connected' : 'disconnected'}`}></span>
                                    <span className="status-text">
                                        {connected ? '🔴 LIVE RESULTS' : '⚫ OFFLINE'}
                                    </span>
                                </div>

                                {/* Survey Title */}
                                <h4 className="mb-3">{surveyData.survey_title}</h4>

                                {/* Survey Info */}
                                <p className="mb-4">
                                    <strong>Survey Code:</strong> {surveyData.survey_code}

                                </p>

                                {/* Results Table */}
                                <div className="results-table-container" style={{ position: 'relative' }}>
                                    {tableLoading && (
                                        <div className="table-loading-overlay">
                                            <div className="spinner small"></div>
                                        </div>
                                    )}
                                    {questionsData.map((questionData, qIndex) => {
                                        const questionTotal = getQuestionTotal(questionData);

                                        return (
                                            <div key={qIndex} className="question-results mb-5">
                                                <h5 className="question-title-table mb-3">
                                                    {questionData.questions?.question_title || `Question ${qIndex + 1}`}
                                                </h5>

                                                <Table striped bordered hover className="results-table">
                                                    <thead>
                                                        <tr>
                                                            <th>Option</th>
                                                            <th>Votes</th>
                                                            <th>Percentage</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {questionData.options && Object.keys(questionData.options).filter(key =>
                                                            key.startsWith('option') && questionData.options[key]
                                                        ).map((optionKey, oIndex) => {
                                                            const votes = getVoteCount(questionData, optionKey);
                                                            const percentage = getPercentage(votes, questionTotal);
                                                            const optionText = getOptionText(questionData, optionKey);

                                                            return (
                                                                <tr key={oIndex} className="result-row">
                                                                    <td className="option-text-table">{optionText}</td>
                                                                    <td className="votes-cell">
                                                                        <span className="vote-count">{votes}</span>
                                                                    </td>
                                                                    <td className="percentage-cell">
                                                                        <div className="percentage-container">
                                                                            <div
                                                                                className="percentage-bar"
                                                                                style={{
                                                                                    width: `${percentage}%`,
                                                                                    backgroundColor: `hsl(${(oIndex * 60) % 360}, 70%, 50%)`
                                                                                }}
                                                                            ></div>
                                                                            <span className="percentage-text">{percentage}%</span>
                                                                        </div>
                                                                    </td>
                                                                </tr>
                                                            );
                                                        })}
                                                    </tbody>
                                                </Table>
                                            </div>
                                        );
                                    })}
                                </div>

                                {/* Footer Message */}
                                <Card.Text className="live-footer-text">
                                    Results update in real-time as new votes come in{' '}
                                    <a href="https://www.surveystud.com/" rel="noopener noreferrer" target="_blank">
                                        SurveyStud.com
                                    </a>
                                    {surveyData.kpi_goal && totalVotes >= surveyData.kpi_goal && (
                                        <div className="goal-achieved-text mt-2">
                                            🎉 Survey goal achieved! ({totalVotes}/{surveyData.kpi_goal})
                                        </div>
                                    )}
                                </Card.Text>

                            </div>
                        </Card.Body>
                    </Card>
                </Container>
            </section>
        </MainTemplate>
    );
};

export default LiveResults; 